import { createContext, startTransition, useContext, useEffect, useState, type ReactNode } from 'react';
import { authSession } from '@/api/axios';
import type { AuthSession, AuthSnapshot } from '@/api/authSession';

const AuthContext = createContext<(AuthSnapshot & { session: AuthSession }) | null>(null);

export function AuthProvider({ children, session = authSession }: { children: ReactNode; session?: AuthSession }) {
  const [snapshot, setSnapshot] = useState<AuthSnapshot>(() => session.getSnapshot());
  useEffect(() => {
    setSnapshot(session.getSnapshot());
    return session.subscribe(() => {
      startTransition(() => setSnapshot(session.getSnapshot()));
    });
  }, [session]);
  useEffect(() => { void session.bootstrap(); }, [session, snapshot.status]);
  useEffect(() => {
    const revalidate = () => {
      if (session.getSnapshot().status === 'authenticated') void session.refresh().catch(() => {});
    };
    window.addEventListener('focus', revalidate);
    return () => window.removeEventListener('focus', revalidate);
  }, [session]);
  return <AuthContext.Provider value={{ ...snapshot, session }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth requires AuthProvider');
  return context;
}
